import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/src/filterFalsy';

import { type useMergedSponsoredAndYmalItems_similarItems$key } from './__generated__/useMergedSponsoredAndYmalItems_similarItems.graphql';
import {
    type useMergedSponsoredAndYmalItems_sponsoredItems$data,
    type useMergedSponsoredAndYmalItems_sponsoredItems$key,
} from './__generated__/useMergedSponsoredAndYmalItems_sponsoredItems.graphql';

const similarItemsFragment = graphql`
    fragment useMergedSponsoredAndYmalItems_similarItems on moreLikeThisItemConnection {
        edges {
            node {
                serviceId
                ...PdpRespSimilarItemsLanding_similarItem
            }
        }
    }
`;

const sponsoredItemsFragment = graphql`
    fragment useMergedSponsoredAndYmalItems_sponsoredItems on SponsoredItems {
        metadata {
            itemId
            impressionTrackerLink
            clickTrackerLink
        }
        items {
            serviceId
            ...PdpRespSimilarItemsLanding_similarItem
        }
    }
`;

// YAML item and sponsored item should be the same
type MergedItem = NonNullable<
    NonNullable<useMergedSponsoredAndYmalItems_sponsoredItems$data['items']>[number]
>;

type SponsoredMetadata = Omit<
    NonNullable<
        NonNullable<useMergedSponsoredAndYmalItems_sponsoredItems$data['metadata']>[number]
    >,
    'itemId' | ' $fragmentType'
>;

type SponsoredMetadataMap = Record<string, SponsoredMetadata>;

type SponsoredMetadataIndexMap = Record<number, SponsoredMetadata> | null;

/**
 * Merges sponsored items and YAML items into a single array. Items are unique.
 * Builds sponsored metadata map by item index.
 * Does not merge items for unpurchasable PDP.
 * @param similarItems YAML items fragment
 * @param sponsoredItems sponsored items fragment
 * @param isPdpTypeDisplayUnpurchasable whether the PDP is displaying unpurchasable items
 * @param purchasableItemCount the number of purchasable items to display
 * @returns the merged items and the index map of the sponsored items
 */
export const useMergedSponsoredAndYmalItems = ({
    similarItems: similarItemsRef,
    sponsoredItems: sponsoredItemsRef,
    isPdpTypeDisplayUnpurchasable,
    isPdpTopYmalSponsoredTestVariant,
    purchasableItemCount,
}: {
    similarItems: useMergedSponsoredAndYmalItems_similarItems$key | null | undefined;
    sponsoredItems: useMergedSponsoredAndYmalItems_sponsoredItems$key | null | undefined;
    isPdpTypeDisplayUnpurchasable: boolean;
    isPdpTopYmalSponsoredTestVariant: boolean;
    purchasableItemCount: number;
}): { mergedItems: MergedItem[]; sponsoredMetadataIndexMap: SponsoredMetadataIndexMap } => {
    const similarItems = useFragment(similarItemsFragment, similarItemsRef);
    const sponsored = useFragment(sponsoredItemsFragment, sponsoredItemsRef);

    const result = useMemo<{
        mergedItems: MergedItem[];
        sponsoredMetadataIndexMap: SponsoredMetadataIndexMap;
    }>(() => {
        const ymalItems = similarItems?.edges
            ? similarItems.edges.map(edge => edge?.node).filter(filterFalsy)
            : [];

        if (isPdpTypeDisplayUnpurchasable) {
            return { mergedItems: ymalItems, sponsoredMetadataIndexMap: null };
        }

        const sponsoredItems = sponsored?.items?.filter(filterFalsy);
        if (!isPdpTopYmalSponsoredTestVariant || !sponsoredItems?.length) {
            return {
                mergedItems: ymalItems.slice(0, purchasableItemCount),
                sponsoredMetadataIndexMap: null,
            };
        }

        const sponsoredMetadataMap =
            sponsored?.metadata?.reduce<SponsoredMetadataMap>((acc, meta) => {
                return {
                    ...acc,
                    ...(meta?.itemId
                        ? {
                              [meta.itemId]: {
                                  impressionTrackerLink: meta.impressionTrackerLink,
                                  clickTrackerLink: meta.clickTrackerLink,
                              },
                          }
                        : {}),
                };
            }, {}) || {};

        const sponsoredItemIds = sponsoredItems.map(item => item.serviceId);
        const sponsoredMetadataIndexMap = sponsoredItemIds.reduce<SponsoredMetadataIndexMap>(
            (acc, itemId, index) => {
                return {
                    ...acc,
                    ...(itemId ? { [index]: sponsoredMetadataMap[itemId] } : {}),
                };
            },
            {}
        );

        const dedupedYmalItems = ymalItems.filter(
            item => !sponsoredItemIds.includes(item.serviceId)
        );

        return {
            mergedItems: [...sponsoredItems, ...dedupedYmalItems].slice(0, purchasableItemCount),
            sponsoredMetadataIndexMap,
        };
    }, [
        similarItems,
        isPdpTypeDisplayUnpurchasable,
        purchasableItemCount,
        sponsored,
        isPdpTopYmalSponsoredTestVariant,
    ]);

    return result;
};
